import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import { supabaseService } from '../../utils/supabaseService';

const BookingDetail = () => {
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [offers, setOffers] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: b, error: be } = await supabase.from('bookings').select('*').eq('id', id).single();
      if (be) throw be;
      setBooking(b);
      const { data: o, error: oe } = await supabase.from('booking_offers').select('*').eq('booking_id', id).order('created_at', { ascending: true });
      if (!oe) setOffers(o || []);
      const { data: c, error: ce } = await supabase.from('commissions').select('*').eq('booking_id', id);
      if (!ce) setCommissions(c || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(()=>{ load(); }, [load]);

  if (loading) return <div className="p-6">Chargement...</div>;
  if (error || !booking) return <div className="p-6 text-red-500">{error || 'Booking introuvable'}</div>;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-bold mb-1">Booking #{booking.id.slice(0,8)}</h1>
        <p className="text-sm text-muted-foreground">Statut: {booking.status} {booking.event_date && `| ${booking.event_date}`}</p>
      </div>
      <div className="bg-card border border-border rounded-lg p-4 space-y-2 text-sm">
        {booking.artist_id && <div>Artist: {booking.artist_id}</div>}
        {booking.venue_id && <div>Venue: {booking.venue_id}</div>}
        {booking.agent_id && <div>Agent: {booking.agent_id}</div>}
        {booking.base_fee && <div>Cachet de base: {booking.base_fee} €</div>}
        {booking.notes_internal && <div>Notes internes: {booking.notes_internal}</div>}
        {booking.status !== 'confirmed' && booking.base_fee && (
          <Button size="sm" onClick={async ()=>{ try { const upd = await supabaseService.confirmBooking({ bookingId: booking.id }); setBooking(upd);} catch(e){setError(e.message);} }}>Confirmer</Button>
        )}
        {booking.status === 'confirmed' && (
          <Button size="sm" variant="outline" onClick={async ()=>{ try { const upd = await supabaseService.markBookingPerformed({ bookingId: booking.id }); setBooking(upd);} catch(e){setError(e.message);} }}>Marquer performed</Button>
        )}
      </div>
      <div>
        <h2 className="font-semibold mb-2">Offres</h2>
        <div className="space-y-2">
          {offers.map(o => (
            <div key={o.id} className="border border-border rounded p-3 text-xs flex justify-between">
              <div>{o.created_at?.slice(0,10)} — Fee: {o.proposed_fee || 'n/a'} € {o.commission_rate && `(comm ${o.commission_rate}%)`}</div>
              {o.message && <div className="text-muted-foreground">{o.message}</div>}
            </div>
          ))}
          {!offers.length && <div className="text-xs text-muted-foreground">Aucune offre.</div>}
        </div>
      </div>
      <div>
        <h2 className="font-semibold mb-2">Commissions</h2>
        <div className="space-y-2">
          {commissions.map(c => (
            <div key={c.id} className="border border-border rounded p-3 text-xs flex justify-between">
              <div>{c.status} {c.amount_calculated && `| ${c.amount_calculated}€`} {c.rate && `(taux ${c.rate}%)`}</div>
              {c.status !== 'paid' && <Button size="sm" variant="outline" onClick={async ()=>{ try { await supabaseService.updateCommissionStatus({ commissionId: c.id, status: 'paid' }); setCommissions(cs=>cs.map(x=>x.id===c.id?{...x,status:'paid',paid_at:new Date().toISOString()}:x)); } catch(e){setError(e.message);} }}>Payer</Button>}
            </div>
          ))}
          {!commissions.length && <div className="text-xs text-muted-foreground">Aucune commission.</div>}
        </div>
      </div>
      {error && <div className="text-xs text-red-500">{error}</div>}
    </div>
  );
};

export default BookingDetail;